import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import {
  Incubation,
  Procedure,
} from "@/services/backend/procedures/procedure/procedure";
import { useListProceduresQuery } from "@/services/backend/procedures/procedure/service";
import { ProcedureType, SampleType } from "@/services/backend/types";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { CollapsibleDataTable } from "@/shared/components/collapsible-data-table";
import {
  DataTableContainer,
  useDataTableController,
} from "@/shared/components/data-table";
import MeasurementInputComponent from "@/shared/components/domain/counting/measurement-input-component";
import { SampleSetNumber } from "@/shared/components/domain/sample-set/sample-set-number";
import { sampleTypes } from "@/shared/components/enum-labels";
import { H4 } from "@/shared/components/typography";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import IncubationMetadataRow from "./incubation-metadata-row";
import ProcedureMetadata from "./procedure-metadata";

const tableColumns = (
  countingDisabled: boolean = false,
): ColumnDef<Procedure>[] => [
  {
    id: "sampleSet",
    accessorKey: "sampleSetId",
    header: "Probensetnummer",
    cell: ({ row }) => {
      const procedureData = row.original;
      return <SampleSetNumber sampleSetId={procedureData.sampleSetId} />;
    },
  },
  {
    id: "sampleType",
    accessorKey: "sampleType",
    header: "Probentyp",
    cell: ({ row }) => {
      const procedureData = row.original;
      return sampleTypes[procedureData.sampleType];
    },
  },
  {
    id: "measurement-1",
    accessorKey: "incubation",
    header: "1. Auszählung",
    cell: ({ row }) => {
      const procedureData = row.original;
      const incubation = procedureData.incubation.find(
        (value: Incubation) => value.sequenceNumber === 1,
      );
      return incubation != null ? (
        <MeasurementInputComponent
          disabled={countingDisabled}
          incubation={incubation}
          procedure={procedureData}
        />
      ) : (
        "Keine Probe gefunden"
      );
    },
  },
  {
    id: "measurement-2",
    accessorKey: "incubation",
    header: "2. Auszählung",
    cell: ({ row }) => {
      const procedureData = row.original;
      const incubation = procedureData.incubation.find(
        (value: Incubation) => value.sequenceNumber === 2,
      );
      return incubation != null ? (
        <MeasurementInputComponent
          disabled={countingDisabled}
          incubation={incubation}
          procedure={procedureData}
        />
      ) : (
        "Keine Probe gefunden"
      );
    },
  },
  /* {
                        id: "comment",
                        accessorKey: "comment",
                        header: "Kommentar",
                        cell: ({ row }) => {
                          const procedureData = row.original
                          return <ProcedureCommentComponent procedure={procedureData} />
                        },
                      }, */
];

interface CountingTableProps {
  ownerId: string;
  ownerType: string;
  procedureType: ProcedureType;
  sampleType: SampleType;
  label: string;
  visibilityState?: VisibilityState;
  immutable?: boolean;
}

function CountingTable({
  ownerId,
  ownerType,
  procedureType,
  sampleType,
  label,
  visibilityState = {},
  immutable,
}: CountingTableProps) {
  const canCount = useFeatureAuth("inspections.counting.count");
  const [procedureOpen, setProcedureOpen] = useState(false);
  const {
    data: list,
    error,
    isLoading,
  } = useListProceduresQuery(
    {
      ownerId: {
        active: true,
        values: [ownerId],
      },
      ownerType: {
        active: true,
        values: [ownerType],
      },
      procedureType: {
        active: true,
        values: [procedureType],
      },
      sampleType: {
        active: true,
        values: [sampleType],
      },
      limit: 0,
      offset: 0,
    },
    { refetchOnMountOrArgChange: true },
  );

  const columns = useMemo(
    () => tableColumns(!canCount || !!immutable),
    [canCount, immutable],
  );
  const table = useDataTableController(
    columns,
    list?.items || [],
    visibilityState,
  );

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <DataTableContainer>
        <CollapsibleDataTable
          table={table}
          columns={columns}
          collapsibleTriggerId="sampleSet"
          renderCollapsibleContent={() => null}
          isLoading
        />
      </DataTableContainer>
    );
  }

  if (list != null && list.items != null && list.items.length > 0) {
    return (
      <div className="flex w-full flex-col space-y-4">
        <Collapsible open={procedureOpen} onOpenChange={setProcedureOpen}>
          <CollapsibleTrigger className="flex flex-row items-center space-x-1">
            <H4>{label}</H4>
            {procedureOpen ? <ChevronDown /> : <ChevronRight />}
          </CollapsibleTrigger>
          <CollapsibleContent>
            {list != null && list.items.length > 0 && (
              <ProcedureMetadata procedure={list.items[0]} />
            )}
          </CollapsibleContent>
        </Collapsible>
        <DataTableContainer>
          <CollapsibleDataTable
            table={table}
            columns={columns}
            collapsibleTriggerId="sampleSet"
            renderCollapsibleContent={(procedure: Procedure) => (
              <IncubationMetadataRow procedure={procedure} />
            )}
          />
        </DataTableContainer>
      </div>
    );
  }

  return <div />;
}

export default CountingTable;
